import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '#/components/ui/accordion';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '#/components/ui/navigation-menu';
import { NavigationMenuItem } from '#/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '#/components/ui/sheet';
import { cn } from '#/lib/utils';
import React from 'react';

import SvgIconChevron from '../svg/icon-chevron';
import SvgIconHeart from '../svg/icon-heart';
import SvgIconNote from '../svg/icon-note';
import SvgIconPlus from '../svg/icon-plus';

type NavLink = {
  href: string;
  label: string;
  isExternal?: boolean;
  children?: NavLink[];
  isLargeHidden?: boolean;
};

const SEARCH_LINKS: NavLink[] = [
  { href: '/', label: 'お買い物で株をもらう' },
  { href: `${import.meta.env.VITE_APPLICATION_TOP_ROUTE}`, label: 'スマホアプリで株をもらう', isLargeHidden: true },
  { href: `${import.meta.env.VITE_SERVICE_TOP_ROUTE}`, label: '登録や申し込みで株をもらう' },
  { href: '/', label: '友達を紹介して株をもらう', isExternal: true }, // リンク未定、外部リンクする予定
  { href: '/', label: 'じゃんけんで株をもらう' }, // リンク未定
];

const NAV_LINKS: NavLink[] = [
  { href: `${import.meta.env.VITE_POINT_HISTORY_ROUTE}`, label: 'カブ活履歴' },
  { href: `${import.meta.env.VITE_GUIDE_ROUTE}`, label: 'カブ活とは？' },
  {
    href: '#',
    label: '探す',
    children: SEARCH_LINKS,
  },
  {
    href: '/',
    label: 'ニュース',
    isExternal: true,
  }, // リンク未定、外部リンクする予定
  { href: `${import.meta.env.VITE_FAQ_ROUTE}`, label: 'FAQ' },
  { href: `${import.meta.env.VITE_CONTACT_ROUTE}`, label: 'サポートデスク' },
];

const SearchMenuItems: React.FC<{ className?: string; itemClassName?: string; linkClassName?: string }> = ({
  className,
  itemClassName,
  linkClassName,
}) => {
  return (
    <ul className={cn('w-full', className)}>
      {SEARCH_LINKS.map((link, index) => (
        <li key={index} className={cn(itemClassName, link.isLargeHidden && 'lg:hidden')}>
          <a
            href={link.href}
            className={cn('text-body-s', linkClassName)}
            {...(link.isExternal ? { target: '_blank' } : {})}>
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  );
};

const NavIcons = (
  <div className="flex items-center gap-4">
    <a href={`${import.meta.env.VITE_FAVORITES_ROUTE}`}>
      <SvgIconHeart
        width={24}
        height={24}
        aria-label="お気に入りショップ"
        className="stroke-grey-900 hover:fill-brand hover:stroke-brand hover:text-brand size-6 flex-none fill-transparent hover:animate-pulse"
      />
    </a>
    <a href={`${import.meta.env.VITE_POINT_HISTORY_ROUTE}`}>
      <div className="relative">
        <SvgIconNote width={24} height={24} aria-label="ポイント獲得履歴" className="hover:text-brand" />
      </div>
    </a>
  </div>
);

export const SiteHeaderLocalNav = () => {
  return (
    <>
      <div className="border-grey-100 z-40 box-content flex h-8 items-stretch border-b bg-white px-4 py-2 lg:px-8 lg:py-4 lg:pt-0">
        {/* Desktop: Navigation menu */}
        <div className="hidden grow items-stretch justify-between gap-6 lg:flex">
          <a href="/" className="text-body-s-bold self-center">
            カブ活
          </a>
          <div className="flex gap-6">
            <NavigationMenu viewport={false}>
              <NavigationMenuList>
                {NAV_LINKS.map((link, index) => (
                  <NavigationMenuItem key={index}>
                    {link.children ? (
                      <>
                        <NavigationMenuTrigger
                          icon={
                            <SvgIconChevron
                              width={16}
                              height={16}
                              aria-label="探すメニューを開く"
                              className="text-grey-900 group-data-[state=open]:text-brand size-4 flex-none transition duration-200 group-data-[state=open]:rotate-180"
                            />
                          }>
                          {link.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <SearchMenuItems
                            className="flex flex-col gap-2"
                            itemClassName="hover:text-brand hover:list-disc"
                            linkClassName="text-body-s data-[active]:text-body-s-bold flex h-6 items-center whitespace-nowrap"
                          />
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <a
                        className="relative flex items-center"
                        href={link.href}
                        {...(link.isExternal ? { target: '_blank' } : {})}>
                        {link.label}
                      </a>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
            {NavIcons}
          </div>
        </div>
        {/* Mobile: Sheet menu */}
        <div className="flex h-8 grow justify-between gap-4 lg:hidden">
          <Sheet>
            <SheetTrigger className="group flex items-center gap-2">
              <span className="text-caption-m-bold">カブ活</span>
              <SvgIconChevron
                width={24}
                height={24}
                aria-label="カブ活メニューを開く"
                className={`text-grey-900 size-4 flex-none transition-transform duration-200 group-data-[state=open]:rotate-180`}></SvgIconChevron>
            </SheetTrigger>
            <SheetContent side="top">
              <SheetHeader className="sr-only">
                <SheetTitle className="sr-only">カブ活メニュー</SheetTitle>
                <SheetDescription className="sr-only">カブ活メニュー</SheetDescription>
              </SheetHeader>
              <div className="text-body-s-bold flex flex-col items-center gap-8 overflow-y-scroll px-8 pt-4 pb-14">
                <div className="w-full">
                  <a href="/" className="border-grey-100 block border-b py-3">
                    トップ
                  </a>
                  {NAV_LINKS.map((link, index) => (
                    <React.Fragment key={index}>
                      {link.children ? (
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="search" className="border-grey-100 border-b">
                            <AccordionTrigger
                              className="py-3"
                              icon={
                                <SvgIconPlus
                                  width={24}
                                  height={24}
                                  aria-label="探すメニューを開く"
                                  className="text-grey-900 size-6 flex-none"
                                />
                              }>
                              {link.label}
                            </AccordionTrigger>
                            <AccordionContent className="pt-2 pb-0">
                              <SearchMenuItems className="pb-10 pl-4" itemClassName="border-grey-100 border-b py-3" />
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ) : (
                        <a
                          href={link.href}
                          className="border-grey-100 block border-b py-3"
                          {...(link.isExternal ? { target: '_blank' } : {})}>
                          {link.label}
                        </a>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </SheetContent>
          </Sheet>
          {NavIcons}
        </div>
      </div>
    </>
  );
};
